'use client';

import React, { useState, useEffect, useRef } from 'react';
import { FAQ_ITEMS } from '@/features/app/constants/faq';

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const delay = Number(el.dataset.delay || 0);
            setTimeout(() => {
              el.classList.remove('reveal-hidden');
              el.classList.add('reveal-visible');
            }, delay);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    itemsRef.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" className="section-padding bg-off-white">
      <div className="max-w-[1320px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Header + context */}
          <div ref={headerRef} data-delay="0" className="reveal-hidden lg:sticky lg:top-28">
            <span className="tag-badge bg-navy/10 text-navy border border-navy/20 mb-5 inline-block">
              Perguntas Frequentes
            </span>
            <h2 className="font-fraunces text-navy text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-6">
              Ainda tem
              <span className="italic"> dúvidas?</span>
            </h2>
            <p className="text-text-muted text-lg font-light leading-relaxed mb-10">
              Respondemos as perguntas mais comuns de donos de empresas de AC sobre como o RetornaAr funciona.
            </p>

            {/* Key message box */}
            <div className="p-6 rounded-2xl bg-navy text-white mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-cyan/20 flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7ECFEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </div>
                <span className="text-white font-semibold">Lembre-se:</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                O RetornaAr <strong className="text-cyan">não envia mensagens para seus clientes</strong>. Ele apenas te avisa quando é hora de entrar em contato. Você mantém total controle da comunicação.
              </p>
            </div>

            <div className="p-6 rounded-2xl border border-border-light">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-navy/10 flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0D1B5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                </div>
                <span className="text-navy font-semibold">Ainda tem dúvida?</span>
              </div>
              <p className="text-text-muted text-sm leading-relaxed mb-4">
                Fala com a gente diretamente. Respondemos no mesmo dia.
              </p>
              <a
                href="mailto:contato@retornaAr.com.br"
                className="inline-flex items-center gap-2 text-navy text-sm font-semibold hover:text-cyan-dark transition-colors"
              >
                contato@retornaAr.com.br
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: FAQ items */}
          <div className="space-y-3">
            {FAQ_ITEMS.map((faq, i) => (
              <div
                key={i}
                ref={(el) => { itemsRef.current[i] = el; }}
                data-delay={i * 80}
                className="reveal-hidden bg-white rounded-2xl border border-border-light overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-navy/2 transition-colors"
                  aria-expanded={openIndex === i}
                  aria-controls={`faq-answer-${i}`}
                >
                  <span className="text-navy font-semibold text-sm md:text-base pr-4 leading-snug">
                    {faq.question}
                  </span>
                  <span
                    className={`shrink-0 w-8 h-8 rounded-full border border-border-light flex items-center justify-center transition-all duration-300 ${
                      openIndex === i ? 'bg-navy border-navy rotate-45' : ''
                    }`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={openIndex === i ? '#fff' : '#0D1B5E'}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                </button>
                <div
                  id={`faq-answer-${i}`}
                  className={`faq-content ${openIndex === i ? 'open' : ''}`}
                >
                  <p className="px-6 pb-6 text-text-muted text-sm leading-relaxed whitespace-pre-line">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;